// =============================================================================
// ExceptionBand - Banda assenza/presenza aggiuntiva sulla griglia, con popup dettagli
// =============================================================================

import { useRef, useState } from 'react';
import { CalendarPlus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { ExceptionTooltip } from './ExceptionTooltip';
import { getReasonVisual, hexToRgba } from '../utils/exceptionStyle';
import type { AvailabilityException } from '../../../utils/availabilityUtils';

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Calcola left/width % di una banda eccezione nella griglia, clampata al range visibile.
 * Restituisce null se l'eccezione cade interamente fuori dal range della griglia.
 */
function exceptionBandStyle(
  exc: AvailabilityException,
  gridStartHour: number,
  gridEndHour: number,
): { left: string; width: string } | null {
  const gridStartMin = gridStartHour * 60;
  const gridEndMin = gridEndHour * 60;
  const startMin = exc.startTime ? timeToMinutes(exc.startTime) : gridStartMin;
  const endMin = exc.endTime ? timeToMinutes(exc.endTime) : gridEndMin;

  const clampedStart = Math.max(startMin, gridStartMin);
  const clampedEnd = Math.min(endMin, gridEndMin);
  if (clampedEnd <= clampedStart) return null;

  const total = gridEndMin - gridStartMin;
  return {
    left: `${((clampedStart - gridStartMin) / total) * 100}%`,
    width: `${((clampedEnd - clampedStart) / total) * 100}%`,
  };
}

interface ExceptionBandProps {
  exception: AvailabilityException;
  gridStartHour: number;
  gridEndHour: number;
}

export function ExceptionBand({ exception, gridStartHour, gridEndHour }: ExceptionBandProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const bandRef = useRef<HTMLDivElement>(null);

  const style = exceptionBandStyle(exception, gridStartHour, gridEndHour);
  if (!style) return null;

  // Assenza: colore + icona in base al motivo (retino diagonale tenue + chip),
  // così è distinguibile a colpo d'occhio dalle normali ore non lavorative
  // (grigio piatto) e se ne intuisce la causa. L'intera banda intercetta
  // l'hover/click — lo slot non è comunque prenotabile.
  if (!exception.isAvailable) {
    const { color, Icon } = getReasonVisual(exception.reason);
    const label = exception.title || exception.reason?.name;
    const hatch = `repeating-linear-gradient(45deg, ${hexToRgba(color, 0.16)} 0, ${hexToRgba(
      color,
      0.16,
    )} 5px, transparent 5px, transparent 11px)`;
    return (
      <div
        ref={bandRef}
        className="absolute top-0 bottom-0 cursor-help overflow-hidden border-l-2"
        style={{
          left: style.left,
          width: style.width,
          borderLeftColor: hexToRgba(color, 0.55),
          backgroundColor: hexToRgba(color, 0.05),
          backgroundImage: hatch,
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <div className="p-0.5">
          <span
            className="inline-flex items-center gap-1 max-w-full px-1 py-0.5 rounded bg-white/80 shadow-sm"
            style={{ color }}
          >
            <Icon className="w-3 h-3 flex-shrink-0" />
            {label && (
              <span className="text-[10px] font-medium leading-none truncate">
                {label}
              </span>
            )}
          </span>
        </div>
        <ExceptionTooltip exception={exception} visible={showTooltip} anchorRef={bandRef} />
      </div>
    );
  }

  // Presenza aggiuntiva: la banda resta cliccabile (lo slot è prenotabile), quindi solo
  // un piccolo indicatore intercetta l'hover per non bloccare i click sulla griglia.
  return (
    <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: style.left, width: style.width }}>
      <div
        ref={bandRef}
        className={cn(
          'absolute top-1 left-1 w-4 h-4 rounded-full bg-emerald-500 text-white',
          'flex items-center justify-center pointer-events-auto cursor-help shadow-sm'
        )}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <CalendarPlus className="w-2.5 h-2.5" />
      </div>
      <ExceptionTooltip exception={exception} visible={showTooltip} anchorRef={bandRef} />
    </div>
  );
}
